import {
	type FetchMarketplaceAvailableMarketsData,
	type FetchMarketplaceAvailableMarketsParams,
	type MarketAvailableMarkets,
	marketplaceApi,
	minutesToMs,
	SociosApi,
	useInfiniteQuery
} from '@capsicum/common';
import { type UseInfiniteQueryOptions, useQuery, type UseQueryOptions } from '@tanstack/react-query';

const fetchAvailableMarkets = async (queryParams?: FetchMarketplaceAvailableMarketsParams, pageParam?: unknown) =>
	(await marketplaceApi).fetchMarketplaceAvailableMarkets(
		{
			...queryParams,
			...(pageParam
				? {
						limit: 10,
						page: pageParam,
						paginationMode: 'simple'
					}
				: { limit: 1000 })
		},
		{
			headers: SociosApi.prepareEDTOHeaders([
				{
					at: '/data/*/baseCurrency/id',
					fetch: 'AclService:Partner:byCurrencyId',
					into: 'partner'
				}
			])
		}
	);

/**
 *
 * @param FetchMarketplaceAvailableMarketsParams
 * @returns MarketAvailableMarkets[]
 */
export const useAvailableMarketsQuery = (
	query?: FetchMarketplaceAvailableMarketsParams,
	options?: Omit<
		UseQueryOptions<FetchMarketplaceAvailableMarketsData, Error, Array<MarketAvailableMarkets>>,
		'queryFn' | 'queryKey'
	>
) =>
	useQuery<FetchMarketplaceAvailableMarketsData, Error, Array<MarketAvailableMarkets>>({
		queryFn: () => fetchAvailableMarkets(query),
		queryKey: ['availableMarkets', query],
		select: data => data.data || [],
		staleTime: minutesToMs(10),
		...options
	});

/**
 *
 * @param FetchMarketplaceAvailableMarketsParams
 * @returns MarketAvailableMarkets[]
 */
export const useAvailableMarketsPaginatedQuery = (
	query?: FetchMarketplaceAvailableMarketsParams,
	options?: Omit<
		UseInfiniteQueryOptions<FetchMarketplaceAvailableMarketsData, Error, Array<MarketAvailableMarkets>>,
		'queryFn' | 'queryKey'
	>
) =>
	useInfiniteQuery<FetchMarketplaceAvailableMarketsData, Error, Array<MarketAvailableMarkets>>({
		queryFn: ({ pageParam }) =>
			fetchAvailableMarkets(
				{
					'details[]': ['currencyImages', 'marketImages'],
					...query
				},
				pageParam
			),
		queryKey: ['availableMarkets', 'paginated', query],
		staleTime: minutesToMs(10),
		...options
	});
